import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';

import { Alert } from '@ds/feedback';
import { FormField, Select } from '@ds/forms';
import { dados } from '@/data/porta';
import type { CanalDeIntegracao } from '@/data/tipos';

export interface SeletorDeCanalProps {
    value: string;
    onChange: (canalId: string) => void;
    /** Só estes tipos de canal. Sem a lista, qualquer canal no ar. */
    tipos?: CanalDeIntegracao['tipo'][];
}

/**
 * Por onde a mensagem sai.
 *
 * **Só canal CONECTADO aparece**, e as três situações sem canal são ditas
 * separadamente: lendo, nenhum no ar e erro ao ler. Erro virava "nenhum canal
 * conectado" — e a clínica ia reconectar um canal que estava no ar.
 */
export function SeletorDeCanal({ value, onChange, tipos }: SeletorDeCanalProps) {
    const id = useId();
    const [canais, setCanais] = useState<CanalDeIntegracao[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const filtro = (tipos ?? []).join(',');

    useEffect(() => {
        let vivo = true;
        setCanais(null);
        setErro(null);
        dados().canaisDaClinica()
            .then((todos) => {
                if (!vivo) return;
                const aceitos = filtro ? filtro.split(',') : null;
                setCanais(todos.filter((c) => c.status === 'conectado' && (!aceitos || aceitos.includes(c.tipo))));
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Tente de novo.'); });
        return () => { vivo = false; };
    }, [filtro]);

    /* Quem tem um canal só não precisa escolher. */
    const primeiro = canais?.[0]?.id;
    useEffect(() => {
        if (!value && primeiro) onChange(primeiro);
    }, [value, primeiro, onChange]);

    if (erro) {
        return <Alert tone="danger" title="Não foi possível ler os canais" dismissible={false}>{erro}</Alert>;
    }
    if (canais === null) {
        return <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Vendo quais canais estão no ar…</p>;
    }
    if (canais.length === 0) {
        return (
            <Alert tone="warning" title="Nenhum canal conectado" dismissible={false}>
                Sem canal no ar a mensagem não tem por onde sair. Conecte em{' '}
                <Link to="/app/configuracoes/integracoes" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                    Configurações › Integrações
                </Link>
                .
            </Alert>
        );
    }
    return (
        <FormField label="Enviar por" htmlFor={id}>
            <Select
                id={id} value={value} onChange={onChange}
                options={canais.map((c) => ({ value: c.id, label: `${c.nome}${c.numero ? ` · ${c.numero}` : ''}` }))}
            />
        </FormField>
    );
}
